import { useEffect } from 'react'
import gameSocket from '../../crash/services/crashSocket'
import useBattleStore from '../store/useBattleStore'
import useAppStore from '../../../store/useAppStore'
import useInventoryStore from '../../../store/useInventoryStore'

const asBattles = (byId) => Object.entries(byId).map(([id, battle]) => ({ ...battle, id }))

export default function useCaseBattleRealtime() {
  const isGuest = useAppStore((state) => state.isGuest)
  useEffect(() => {
    useBattleStore.setState({ battles: [], battlesById: {}, currentBattle: null, availableCases: [], hasSnapshot: false })
    let inventoryRequest = null
    let inventoryQueued = false
    const refreshInventory = () => {
      if (inventoryRequest) {
        inventoryQueued = true
        return
      }
      inventoryRequest = useInventoryStore.getState().loadInventory({ silent: true })
        .finally(() => {
          inventoryRequest = null
          if (inventoryQueued) {
            inventoryQueued = false
            refreshInventory()
          }
        })
    }
    const handlers = {
      'casebattle:state': (data) => {
        if (data.server_time) gameSocket.clockOffset = Number(data.server_time) - Date.now()
        const byId = Object.fromEntries(Object.entries(data.battles || {}).map(([id, battle]) => [id, { ...battle, id }]))
        useBattleStore.setState({
          engine: data.engine, hasSnapshot: data.engine === 'node', isConnected: true,
          isModeDisabled: Boolean(data.disabled), limits: data.limits || {},
          battlesById: byId,
          battles: data.engine === 'node' ? asBattles(byId) : []
        })
        if (data.engine === 'worker') useBattleStore.getState().loadBattles()
      },
      'casebattle:battle': (battle) => {
        if (!battle?.id) return
        useBattleStore.setState((state) => {
          const byId = { ...state.battlesById, [battle.id]: battle }
          return { battlesById: byId, ...(state.engine === 'node' ? { battles: asBattles(byId) } : {}) }
        })
      },
      'casebattle:remove': ({ id }) => {
        useBattleStore.setState((state) => {
          const byId = { ...state.battlesById }
          delete byId[id]
          return { battlesById: byId, ...(state.engine === 'node' ? { battles: asBattles(byId) } : {}) }
        })
      },
      'casebattle:engine': ({ mode }) => {
        useBattleStore.setState({ engine: mode, hasSnapshot: false, battles: [], battlesById: {} })
        if (mode === 'worker') useBattleStore.getState().loadBattles()
        gameSocket.requestState('casebattle')
      },
      'casebattle:disabled': () => useBattleStore.setState({ isModeDisabled: true }),
      'user:balance': ({ balance }) => {
        if (!gameSocket.authToken || gameSocket.resolveAuthToken() !== gameSocket.authToken || balance == null || !Number.isFinite(Number(balance))) return
        useAppStore.setState((state) => ({ balance: Number(balance), user: state.user ? { ...state.user, balance: Number(balance) } : state.user }))
      },
      'user:inventory': () => {
        if (gameSocket.authToken && gameSocket.resolveAuthToken() === gameSocket.authToken) refreshInventory()
      },
      'user:alert': ({ game, code }) => {
        if (game !== 'casebattle' || code !== 'engine_switched') return
        useBattleStore.setState({ hasSnapshot: false })
        gameSocket.requestState('casebattle')
      }
    }
    const onConnect = () => useBattleStore.setState({ isConnected: false })
    const onDisconnect = () => useBattleStore.setState({ isConnected: false })
    Object.entries(handlers).forEach(([event, handler]) => gameSocket.on(event, handler))
    gameSocket.onConnect(onConnect)
    gameSocket.onDisconnect(onDisconnect)
    const profileEngine = useAppStore.getState().user?.game_engines?.casebattle
    useBattleStore.setState({
      engine: profileEngine || useBattleStore.getState().engine,
      hasSnapshot: profileEngine === 'worker', isConnected: false
    })
    gameSocket.subscribe('casebattle')
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => gameSocket.off(event, handler))
      gameSocket.offConnect(onConnect)
      gameSocket.offDisconnect(onDisconnect)
      gameSocket.unsubscribe('casebattle')
      useBattleStore.setState({ isConnected: false, hasSnapshot: false })
    }
  }, [isGuest])
}
